import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

/**
 * Admin Partners Panel.
 * Visible only to users with is_admin = 1.
 *
 * Flow:
 *   - Admin clicks "Add partner" → fills a form → submits.
 *   - Server creates the partner row, auto-provisions a system NChat user,
 *     issues an API key + secret, returns the credentials ONCE.
 *   - This page surfaces them in a copy-able modal. The secret is never
 *     retrievable later — admin must hand it to the partner securely.
 */
export default function PartnersAdminPage() {
  const { user } = useAuth();
  const [filter, setFilter]       = useState('active');
  const [list, setList]           = useState([]);
  const [open, setOpen]           = useState(null);
  const [credentials, setCreds]   = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading]     = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listPartners(filter);
      setList(res.data?.data?.partners || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [filter]);

  if (!user?.is_admin) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h2 className="auth-title">Admins only</h2>
          <p className="auth-subtitle">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <h1>Partner Apps</h1>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <div className="admin-tabs">
            {['active', 'suspended', 'rejected', ''].map(s => (
              <button key={s || 'all'}
                      className={`tab-btn ${filter === s ? 'active' : ''}`}
                      onClick={() => setFilter(s)}>
                {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            + Add partner
          </button>
        </div>
      </header>

      {credentials && (
        <CredentialsModal credentials={credentials} onClose={() => { setCreds(null); refresh(); }} />
      )}

      {showCreate && (
        <CreatePartnerModal
          onClose={() => setShowCreate(false)}
          onCreated={(creds) => { setShowCreate(false); setCreds(creds); }}
        />
      )}

      <div className="admin-body">
        <aside className="partner-list">
          {loading
            ? <div className="loading-row">Loading…</div>
            : list.length === 0
              ? <div className="empty-state"><div>No partners</div></div>
              : list.map(p => (
                  <div key={p.id}
                       className={`partner-list-row ${open?.partner?.id === p.id ? 'active' : ''}`}
                       onClick={() => adminAPI.getPartner(p.id).then(r => setOpen(r.data?.data))}>
                    <div className="row-name">{p.business_name}</div>
                    <div className="row-meta">
                      <span className={`status-pill status-${p.status}`}>{p.status}</span>
                      <span className="row-phone">{p.website || p.contact_phone || ''}</span>
                    </div>
                    <div className="row-date">created {new Date(p.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
        </aside>
        <main className="partner-detail">
          {!open
            ? <div className="empty-state"><div>Select a partner to view details</div></div>
            : <PartnerDetail data={open}
                             onChanged={() => { refresh(); }}
                             onCredentialsIssued={creds => setCreds(creds)} />}
        </main>
      </div>
    </div>
  );
}

function PartnerDetail({ data, onChanged, onCredentialsIssued }) {
  const p = data.partner;
  const [busy, setBusy] = useState(false);

  const action = async (fn, ...args) => {
    setBusy(true);
    try {
      const res = await fn(...args);
      const creds = res.data?.data?.api_credentials;
      if (creds) onCredentialsIssued(creds);
      else onChanged();
    } finally { setBusy(false); }
  };

  return (
    <div className="partner-detail-card">
      <h2>{p.business_name}</h2>
      <div className="kv-grid">
        <div><span className="lbl">Slug</span><span className="mono">{p.slug}</span></div>
        <div><span className="lbl">Status</span><span className={`status-pill status-${p.status}`}>{p.status}</span></div>
        <div><span className="lbl">Owner</span><span>{p.owner_name || '—'}</span></div>
        <div><span className="lbl">Contact phone</span><span>{p.contact_phone || '—'}</span></div>
        <div><span className="lbl">Email</span><span>{p.contact_email || '—'}</span></div>
        <div><span className="lbl">Website</span><span>{p.website ? <a href={p.website} target="_blank" rel="noopener noreferrer">{p.website}</a> : '—'}</span></div>
        <div><span className="lbl">Daily limit</span><span>{p.daily_limit?.toLocaleString()} req/day</span></div>
        <div><span className="lbl">Per-min limit</span><span>{p.per_min_limit} req/min</span></div>
        <div><span className="lbl">IP allowlist</span><span className="mono">{p.allowed_ips || '—'}</span></div>
        <div><span className="lbl">System user</span><span>#{p.system_user_id}</span></div>
        <div><span className="lbl">Created</span><span>{new Date(p.created_at).toLocaleString()}</span></div>
        <div><span className="lbl">Approved at</span><span>{p.approved_at ? new Date(p.approved_at).toLocaleString() : '—'}</span></div>
      </div>
      {p.description && <div className="info-block"><strong>Description</strong><p>{p.description}</p></div>}

      {data.stats && Number(data.stats.total_calls) > 0 && (
        <div className="stats-row">
          <div><div className="stat-num">{data.stats.total_calls}</div><div>Total calls</div></div>
          <div><div className="stat-num">{data.stats.valid_calls || 0}</div><div>OK</div></div>
          <div><div className="stat-num">{data.stats.failed_calls || 0}</div><div>Failed</div></div>
        </div>
      )}

      <div className="action-bar">
        {p.status === 'active' && <>
          <button className="btn btn-secondary" disabled={busy} onClick={() => action(adminAPI.rotateKeys, p.id)}>Rotate keys</button>
          <button className="btn btn-danger"    disabled={busy} onClick={() => action(adminAPI.suspendPartner, p.id)}>Suspend</button>
        </>}
        {p.status === 'suspended' && (
          <button className="btn btn-primary" disabled={busy} onClick={() => action(adminAPI.reactivate, p.id)}>Reactivate</button>
        )}
      </div>

      {data.keys?.length > 0 && (
        <div className="info-block">
          <strong>API keys</strong>
          <ul className="activity-list">
            {data.keys.map(k => (
              <li key={k.id}>
                <span className="mono">{k.key_id}</span>
                <span style={{ color: 'var(--text-muted)' }}>{k.label}</span>
                <span style={{ color: 'var(--text-muted)' }}>
                  {k.revoked_at ? `revoked ${new Date(k.revoked_at).toLocaleDateString()}`
                                : (k.last_used_at ? `last used ${new Date(k.last_used_at).toLocaleString()}` : 'never used')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.recent_activity?.length > 0 && (
        <div className="info-block">
          <strong>Recent API activity</strong>
          <ul className="activity-list">
            {data.recent_activity.slice(0, 10).map((a, i) => (
              <li key={i}>
                <span className="mono">{a.method} {a.endpoint}</span>
                <span className={`status-pill ${a.signature_valid ? 'status-active' : 'status-rejected'}`}>
                  {a.response_status}
                </span>
                <span style={{ color: 'var(--text-muted)' }}>{a.ip}</span>
                <span style={{ color: 'var(--text-muted)' }}>{new Date(a.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CreatePartnerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    business_name: '', owner_name: '', contact_email: '', contact_phone: '',
    website: '', description: '', logo_url: '',
    daily_limit: 5000, per_min_limit: 60, allowed_ips: '',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const res = await adminAPI.createPartner({
        ...form,
        contact_phone: form.contact_phone.replace(/[^0-9]/g, ''),
        daily_limit:   parseInt(form.daily_limit, 10)   || 5000,
        per_min_limit: parseInt(form.per_min_limit, 10) || 60,
      });
      if (res.data?.success) {
        onCreated(res.data.data.api_credentials);
      } else {
        setError(res.data?.message || 'Create failed');
      }
    } catch (e2) {
      setError(e2.response?.data?.message || 'Network error');
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <h2 style={{ marginTop: 0 }}>Add partner</h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
          Creates an active partner immediately and issues an API key. The secret is shown once after creation.
        </p>
        {error && <div className="form-error">⚠️ {error}</div>}
        <form onSubmit={submit}>
          <Field label="Business name *"  value={form.business_name} onChange={v => update('business_name', v)} required />
          <Field label="Owner name"       value={form.owner_name}    onChange={v => update('owner_name', v)} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Contact phone"  value={form.contact_phone} onChange={v => update('contact_phone', v)} placeholder="+91 …" />
            <Field label="Email"          type="email" value={form.contact_email} onChange={v => update('contact_email', v)} />
          </div>
          <Field label="Website"          type="url" placeholder="https://nchat.norissolutions.com" value={form.website} onChange={v => update('website', v)} />
          <Field label="Logo URL"         type="url" value={form.logo_url} onChange={v => update('logo_url', v)} />
          <Field label="Description"      textarea rows={3} value={form.description} onChange={v => update('description', v)} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Daily limit"   type="number" value={form.daily_limit}   onChange={v => update('daily_limit', v)} />
            <Field label="Per-minute"    type="number" value={form.per_min_limit} onChange={v => update('per_min_limit', v)} />
          </div>
          <Field label="IP allowlist (comma separated, optional)" value={form.allowed_ips} onChange={v => update('allowed_ips', v)} placeholder="1.2.3.4, 5.6.7.8" />

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy} style={{ flex: 1 }}>
              {busy ? 'Creating…' : 'Create partner & issue key'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, textarea, rows, type='text', placeholder, required }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {textarea
        ? <textarea className="form-input" rows={rows || 2} value={value}
                    onChange={e => onChange(e.target.value)} placeholder={placeholder} />
        : <input    className="form-input" type={type} value={value}
                    onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required} />}
    </div>
  );
}

function CredentialsModal({ credentials, onClose }) {
  const [copied, setCopied] = useState('');
  const copy = (val, tag) => {
    navigator.clipboard?.writeText(val);
    setCopied(tag);
    setTimeout(() => setCopied(''), 1500);
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <h2 style={{ marginTop: 0 }}>🔐 API credentials issued</h2>
        <p>
          Send these to the partner securely (password manager link, encrypted email, in person).
          <br/><strong>The secret is shown ONCE</strong> — you cannot retrieve it later.
          You can rotate any time from the partner detail panel.
        </p>
        {[
          ['Key ID', credentials.key_id, 'key'],
          ['Secret', credentials.secret, 'secret'],
        ].map(([label, val, tag]) => val && (
          <div key={tag} className="cred-row">
            <div className="cred-label">{label}</div>
            <code className="cred-value">{val}</code>
            <button className="btn btn-sm btn-secondary" onClick={() => copy(val, tag)}>
              {copied === tag ? 'Copied!' : 'Copy'}
            </button>
          </div>
        ))}
        <button className="btn btn-primary" onClick={onClose} style={{ width: '100%', marginTop: 16 }}>
          I've shared these securely — close
        </button>
      </div>
    </div>
  );
}
